import { createFileRoute, Link, notFound } from "@tanstack/react-router";

import { PageBlocks } from "@/components/content/PageBlocks";
import { Footer } from "@/components/layout/Footer";
import { Header } from "@/components/layout/Header";
import { findTeamMember } from "@/data/team";
import { pageBlocks } from "@/lib/content";

export const Route = createFileRoute("/team/$slug")({
  loader: ({ params }) => {
    const member = findTeamMember(params.slug);
    if (!member) throw notFound();
    return { member };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: "Team — Light of Life International" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const title = `${loaderData.member.name} — Light of Life International`;
    const description = `Meet ${loaderData.member.name}, part of the Light of Life International ministry team.`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "profile" },
        { property: "og:url", content: `https://loli.ardent.dev/team/${params.slug}` },
      ],
      links: [{ rel: "canonical", href: `https://loli.ardent.dev/team/${params.slug}` }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Person",
            name: loaderData.member.name,
            ...(loaderData.member.role ? { jobTitle: loaderData.member.role } : {}),
            image: `https://loli.ardent.dev${loaderData.member.image}`,
            url: `https://loli.ardent.dev/team/${params.slug}`,
            worksFor: {
              "@type": "Organization",
              name: "Light of Life International",
              url: "https://loli.ardent.dev/",
            },
          }),
        },
      ],
    };
  },
  notFoundComponent: TeamNotFound,
  component: TeamMemberPage,
});

function TeamNotFound() {
  return (
    <div className="flex min-h-dvh flex-col bg-background">
      <Header />
      <main id="main" className="container-site max-w-3xl section-y flex-1">
        <h1 className="text-4xl text-navy">Team member not found</h1>
        <Link
          to="/about"
          className="mt-6 inline-block font-heading text-sm uppercase tracking-widest underline"
        >
          Back to Team
        </Link>
      </main>
      <Footer />
    </div>
  );
}

function TeamMemberPage() {
  const { member } = Route.useLoaderData();
  // Keep only the biography paragraphs: drop titles, photos and the
  // short link fragments the source markup emitted as separate blocks.
  const blocks = pageBlocks(member.contentKey).filter((b) => {
    if (b.t === "img" || b.t === "h1" || b.t === "h2") return false;
    const value = "text" in b ? b.text.trim() : "";
    return value.length > 60;
  });

  return (
    <div className="flex min-h-dvh flex-col bg-background">
      <Header overlay />

      {/* Short photo band with the member's name, matching the source site */}
      <section className="relative flex min-h-[220px] items-center justify-center overflow-hidden">
        <img
          src="/media/LOLI_Crusades_22.jpg"
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="hero-scrim absolute inset-0" aria-hidden />
        <div className="container-site relative pt-16 pb-6 text-center">
          <h1 className="text-4xl text-white sm:text-6xl">{member.name}</h1>
          {member.role ? <p className="eyebrow mt-3 text-sm text-white/90">{member.role}</p> : null}
        </div>
      </section>

      <main id="main" className="flex-1">
        <section className="section-y">
          <div className="container-site grid items-start gap-12 md:grid-cols-2">
            {/* Diamond portrait: square image rotated inside a rotated frame */}
            <div className="flex justify-center">
              <div className="w-[300px] rotate-45 overflow-hidden sm:w-[360px]">
                <div className="aspect-square -rotate-45 scale-[1.42]">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="h-full w-full object-cover"
                  />
                </div>
              </div>
            </div>

            <div className="md:pt-6">
              <PageBlocks blocks={blocks} />
              <Link
                to="/about"
                hash="team"
                className="mt-10 inline-flex items-center gap-2 font-heading text-base uppercase tracking-widest text-brand-foreground"
              >
                <span aria-hidden>&lsaquo;</span> Back to Team
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
